/** @file Shared apply of the weight commands: skinned mesh lookup, new lists, no-op test, bindPose sync. */
import type { Draft } from 'immer';
import type { Doc, Id, MeshAtt, Warning } from '@pose2d/format/types';
import type { CommandContext } from '../commandContext';
import { meshDraft } from './meshDraft';
import { sameWeights } from './sameWeights';
import { setupWorldOf } from './setupWorldOf';
import { skinnedDraft } from './skinnedDraft';
import { staleBindBones } from './staleBindBones';
import { syncBindPose } from './syncBindPose';
import { weightedBones } from './weightedBones';
import { writeChanged } from './writeChanged';

type Skinned = Extract<MeshAtt['geometry'], { kind: 'skinned' }>;

type Influences = [Id, number][];

/** True when two vertices hold the same [bone, weight] pairs in the same order. */
const sameRow = (a: Influences, b: Influences): boolean => sameWeights([a], [b]);

/** The weight of `bone` in one vertex's influences (0 when absent). */
const weightIn = (row: Influences, bone: Id): number => row.find(([id]) => id === bone)?.[1] ?? 0;

/** Bones whose weight differs between `before` and `after` on some vertex (a bone missing from a row weighs 0). */
function reweighted(before: readonly Influences[], after: readonly Influences[]): Set<Id> {
  const bones = new Set<Id>();
  for (let v = 0; v < Math.max(before.length, after.length); v++) {
    const a = before[v] ?? [];
    const b = after[v] ?? [];
    if (sameRow(a, b)) continue;
    for (const [bone] of [...a, ...b]) if (weightIn(a, bone) !== weightIn(b, bone)) bones.add(bone);
  }
  return bones;
}

/** The INFO of a weight edit on bones bound before their setup pose changed: it names the mesh, the bones, the fix. */
function staleInfo(doc: Doc, mesh: string, stale: readonly Id[]): Warning {
  const bones = stale.map((bone) => doc.bones[bone]?.name ?? bone).join(', ');
  const message = `${mesh}: the setup pose of ${bones} changed after the mesh was bound (stale bind pose), so these weights move vertices at the setup pose; call mesh.rebindMesh {target: '${mesh}'} first to keep the setup look`;
  return { code: 'INFO', message, params: { mesh, bones }, path: 'target' };
}

/**
 * Resolves `target` (VALIDATION for regions and rigid meshes), writes the vertex lists of `edit` that changed (one
 * patch per vertex for a small edit, R17-01b), syncs bindPose and stamps updatedAt; unchanged is a no-op. Weights
 * changed for a bone whose bind pose differs from its setup world add one INFO (T-473); the edit itself is unchanged.
 */
export function editWeights(draft: Draft<Doc>, ctx: CommandContext, target: string, edit: (g: Skinned, mesh: MeshAtt) => Influences[]): void {
  const { mesh } = meshDraft(draft, ctx, target);
  const g = skinnedDraft(mesh);
  const next = edit(g, mesh);
  let worldOf: ((bone: Id) => Float64Array) | undefined;
  // Setup worlds are computed only for a reweighted bone that has a bind pose, or a newly weighted bone.
  const world = (bone: Id): Float64Array => (worldOf ??= setupWorldOf(draft))(bone);
  const changed = [...reweighted(g.weights, next)].filter((bone) => Object.hasOwn(g.bindPose, bone));
  const stale = changed.length === 0 ? [] : staleBindBones(g.bindPose, world, changed);
  const bound = weightedBones(next).join(',') === Object.keys(g.bindPose).sort().join(',');
  const wrote = writeChanged(g.weights, next, sameRow, (all) => {
    g.weights = all;
  });
  if (bound && !wrote) return;
  // bindPose is rebuilt only when its key set changes.
  if (!bound) syncBindPose(g, world);
  if (stale.length > 0) ctx.warnings.push(staleInfo(draft, mesh.name, stale));
  draft.meta.updatedAt = ctx.now;
}
